import type { TokenRequest, TokenResource } from "@sarva/api-client";
import { api, signIn as storeCredentials } from "@/shared/api";
import { rememberTenantId } from "./tenant";

/**
 * ⚠️  DEVELOPMENT AUTHENTICATION ONLY — THIS IS NOT PRODUCTION SIGN-IN.  ⚠️
 *
 * `POST /api/v1/auth/token` takes a tenant id and an email address and returns
 * a bearer token. It verifies **no credential**: there is no password, no
 * second factor, and no proof the caller is the person named. The endpoint is
 * registered only when `ENVIRONMENT=dev` and the generated client's own type
 * says so — "Development token exchange. Real environments use OIDC (doc 23
 * §2) and never this."
 *
 * What replaces it (doc 23 §2): an OIDC authorization-code flow with PKCE
 * against the tenant's identity provider, MFA enforced for any identity holding
 * a write permission, and a short-lived access token paired with an `HttpOnly`,
 * `Secure`, `SameSite=Strict` refresh cookie (doc 23 §11a). When that lands,
 * this module is deleted — not adapted. Nothing outside `features/auth`
 * imports it, so the blast radius of that deletion is one folder.
 *
 * Until then: never point a build at a non-development API with this in it, and
 * never add a code path that calls it outside a sign-in the user asked for.
 */
export async function exchangeDevToken(
  request: TokenRequest,
): Promise<TokenResource> {
  const envelope = await api.issueToken({ body: request });
  const token = envelope.data;

  // Which tenant this session belongs to, so the next page load can present the
  // session cookie for it. See `restorableTenantId`.
  rememberTenantId(token.tenant_id);

  // (H) doc 23 §11a: the token goes into memory and nowhere else.
  storeCredentials({
    accessToken: token.access_token,
    expiresAt: token.expires_at,
    actorId: token.actor_id,
    tenantId: token.tenant_id,
    sessionId: token.session_id,
    // Not from the response: `/me` has no display name or email to show, so the
    // account menu falls back to what the person typed. See `Credentials`.
    email: request.email,
  });

  return token;
}
